'use client'
import { usePathname, useRouter } from 'next/navigation'
import type React from 'react'
import { useContext, useEffect } from 'react'
import { appTrace } from '~/utils'

import { AuthActorContext } from './AuthProvider'
import { useAppAuth } from './hooks'
import { useSelector } from '@xstate/react'
import { Route } from 'next'
import { useSession } from 'next-auth/react'
import { createNavigationHelper } from './navigation'
import { NavigationContext } from './NavigationContext'

/**
 * Component that handles authentication-based routing and redirects.
 * Ensures users are directed to the appropriate pages based on their auth state.
 */
function AuthRouterRedirector({ children }: React.PropsWithChildren) {
  const { isStableAuthState } = useAppAuth()
  const pathname = usePathname()
  const router = useRouter()
  const navigation = createNavigationHelper(router)
  const { needsOnboarding, allowsOnboardingCompletion, isSignedIn, setOnboardingCompletionOptedOut } = useAppAuth()
  const { actor: authActor } = useContext(AuthActorContext)

  const isSuspended = useSelector(authActor, (state) => {
    return state.matches('registration.suspended')
  })

  useEffect(() => {
    // this is needed for when users create multiple accounts
    // otherwise the final "explore dome" button will not work
    if (!isSignedIn || !isStableAuthState || !needsOnboarding) return
    setOnboardingCompletionOptedOut(false)
  }, [isStableAuthState, isSignedIn, needsOnboarding, setOnboardingCompletionOptedOut])

  // Handle redirects based on auth state
  useEffect(() => {
    if (!isStableAuthState) return

    if (isSuspended && !pathname.startsWith('/suspended')) {
      appTrace('authRouter: user suspended, redirecting')
      navigation.navigate({
        name: 'suspended_redirect',
        route: '/suspended',
      })
      return
    }

    if (needsOnboarding && !pathname.startsWith('/onboarding')) {
      appTrace('authRouter: needs onboarding, redirecting')
      navigation.navigate({
        name: 'onboarding_redirect',
        route: '/onboarding',
      })
      return
    }

    if (isSignedIn) {
      if (pathname.startsWith('/start') || pathname.startsWith('/auth')) {
        appTrace('authRouter: signedIn and onboarded, redirect to /')
        navigation.navigate({
          name: 'signed_in_redirect',
          route: '/',
        })
        return
      }
      appTrace('authRouter: user signedIn and does not need onboarding, noop')
      return
    }

    appTrace('authRouter: stable auth state, no redirect', {
      needsOnboarding,
    })
  }, [isSuspended, needsOnboarding, pathname, isStableAuthState, isSignedIn, navigation])

  /**
   * Handles redirection after onboarding completion.
   *
   * Note: The setTimeout wrapper around navigation calls is used to ensure
   * navigation happens in the next event loop tick. This helps prevent race conditions
   * where state updates might not be fully processed before navigation occurs.
   *
   * This was particularly important for desktop browsers where immediate navigation
   * calls could sometimes be ignored or fail silently. The small delay ensures
   * all state updates are complete before attempting navigation.
   */
  useEffect(() => {
    if (!isStableAuthState || isSuspended) return

    const doesPathAllowForOnboardingCompletion =
      pathname.startsWith('/onboarding') || pathname === '/' || pathname.startsWith('/auth')
    appTrace('authRouter: considering redirect_to', {
      doesPathAllowForOnboardingCompletion,
      allowsOnboardingCompletion,
      currentPath: pathname,
    })

    if (allowsOnboardingCompletion && doesPathAllowForOnboardingCompletion) {
      appTrace('authRouter: onboarding finished, redirecting')

      const redirectUrl = localStorage.getItem('redirect_to')

      if (redirectUrl) {
        appTrace('authRouter: redirect_to found, redirecting to', redirectUrl)
        localStorage.removeItem('redirect_to')
        setTimeout(() => {
          navigation.navigate({
            name: 'onboarding_redirect_to',
            route: redirectUrl as Route,
          })
        }, 0)
      } else {
        appTrace('authRouter: no redirect url, redirecting to /help')
        setTimeout(() => {
          navigation.navigate({
            name: 'onboarding_redirect_to_help',
            route: '/help',
          })
        }, 0)
      }
    }
  }, [allowsOnboardingCompletion, pathname, isStableAuthState, isSuspended, navigation])

  return children
}

/**
 * Main authentication router component that handles session updates and routing.
 */
export function AuthRouter({ children }: React.PropsWithChildren) {
  const router = useRouter()
  const navigation = createNavigationHelper(router)
  const { actor: authActor } = useContext(AuthActorContext)
  const { data: session } = useSession()

  // Update auth state based on session changes
  useEffect(() => {
    if (session) {
      authActor.send({
        type: 'NEXTAUTH_TOKEN_UPDATE',
        data: { token: (session as unknown as { jwtToken: string }).jwtToken },
      })
    } else {
      authActor.send({
        type: 'NEXTAUTH_TOKEN_UNAVAILABLE',
      })
    }
  }, [session, authActor])

  return (
    <NavigationContext.Provider value={navigation}>
      <AuthRouterRedirector>{children}</AuthRouterRedirector>
    </NavigationContext.Provider>
  )
}
